import { Button, Card, Select, Space, Tag } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useState } from "react";
import { Dialog as ZhDialog } from "zhihao-ui-react";

import { E_TYPE } from "@zhihaoscm/meta/src/platClue";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { formatSecondsToMinuteSecond } from "./utils";
import { AdminTableEmpty } from "@/views/components/TableEmpty";

export interface ClueDashboardRow {
    platClue?: {
        id?: number | string;
        state?: number;
        stayDurationSeconds?: number;
    };
    customer?: { mobile?: string; createdTime?: string };
    shippingRequirementPlat?: {
        sourcePortName?: string;
        destinationPortName?: string;
        freightTons?: number;
        loadDate?: string;
        loadDays?: number;
    };
    shippingRequirementAccept?: {
        agentContact?: string;
        agentMobile?: string;
        captainEnterprise?: { realName?: string; mobile?: string };
        quoteIdentity?: number | string;
        shipInfos?: Array<{ ton?: number }>;
    };
    emptyShipVo?: {
        originPorts?: Array<{ portName?: string }>;
        destPorts?: Array<{ portName?: string }>;
        emptyShip?: { emptyStartTime?: string; emptyEndTime?: string };
    };
}

/** Vue 大屏接口的分组返回契约：1 注册用户、2 找船需求、3 报价、4 空船。 */
export type ClueDashboardData = Record<E_TYPE, ClueDashboardRow[]>;
export type ClueDashboardUnPlayed = Array<{ platClues?: ClueDashboardRow[] }> | ClueDashboardRow[];

export async function loadClueDashboardData(): Promise<ClueDashboardData> {
    const { platClueService } = await import("@/api/services/platClueService");
    return (await platClueService.getPlatClueScreen()) as ClueDashboardData;
}

export async function loadClueDashboardUnPlayed(): Promise<ClueDashboardUnPlayed> {
    const { platClueService } = await import("@/api/services/platClueService");
    return (await platClueService.getPlatClueUnPlayed()) as ClueDashboardUnPlayed;
}

interface ClueDashboardPageProps {
    loadScreen?: () => Promise<ClueDashboardData>;
    loadUnPlayed?: () => Promise<ClueDashboardUnPlayed>;
}

interface DashboardTable {
    type: E_TYPE;
    title: string;
    headers: string[];
    renderRow: (row: ClueDashboardRow) => string[];
}

const DEFAULT_VALUE = "--";
const SPEECH_COUNT_STORAGE_KEY = "clues-dashboard-num";
const SPEECH_COUNT_OPTIONS = [1, 2, 3].map(value => ({ label: `${value}次`, value }));

/** 读取旧 Vue 线索大屏的本地播报次数，非法值回退为 1 次。 */
const readSpeechCount = (): number => {
    const stored = Number(localStorage.getItem(SPEECH_COUNT_STORAGE_KEY));
    return SPEECH_COUNT_OPTIONS.some(option => option.value === stored) ? stored : 1;
};

const statusLabel = (state?: number) => ({ 0: "未处理", 1: "持续跟进", 2: "已处理" })[Number(state)] || DEFAULT_VALUE;

const formatDuration = (seconds?: number) => {
    if (!seconds) return DEFAULT_VALUE;
    // 旧 Vue 大屏使用 m/s（例如 65 秒展示为 1m5s），保持播报屏数据口径一致。
    return formatSecondsToMinuteSecond(seconds);
};

const valueOrDefault = (value: number | string | undefined) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

const tables: DashboardTable[] = [
    {
        type: E_TYPE.NEW_USER,
        title: "新注册用户",
        headers: ["注册手机号", "注册时间", "滞留时间", "处理状态"],
        renderRow: row => [
            valueOrDefault(row.customer?.mobile),
            row.customer?.createdTime ? dateFormat(row.customer.createdTime, DATETIME_PICKER_FORMAT) : DEFAULT_VALUE,
            formatDuration(row.platClue?.stayDurationSeconds),
            statusLabel(row.platClue?.state)
        ]
    },
    {
        type: E_TYPE.NEW_SHIPPING_REQUIREMENT_PLAT,
        title: "新增找船需求",
        headers: ["始发地", "目的地", "装货吨数", "预计装载日期", "滞留时间", "处理状态"],
        renderRow: row => [
            valueOrDefault(row.shippingRequirementPlat?.sourcePortName),
            valueOrDefault(row.shippingRequirementPlat?.destinationPortName),
            valueOrDefault(row.shippingRequirementPlat?.freightTons),
            row.shippingRequirementPlat?.loadDate
                ? `${dateFormat(row.shippingRequirementPlat.loadDate, DATE_PICKER_FORMAT)} +${row.shippingRequirementPlat.loadDays || 0}天`
                : DEFAULT_VALUE,
            formatDuration(row.platClue?.stayDurationSeconds),
            statusLabel(row.platClue?.state)
        ]
    },
    {
        type: E_TYPE.NEW_SHIPPING_REQUIREMENT_ACCEPT,
        title: "新报价信息",
        headers: ["联系人", "联系电话", "装货吨数", "滞留时间", "处理状态"],
        renderRow: row => [
            valueOrDefault(
                row.shippingRequirementAccept?.agentContact ||
                    row.shippingRequirementAccept?.captainEnterprise?.realName
            ),
            valueOrDefault(
                row.shippingRequirementAccept?.agentMobile || row.shippingRequirementAccept?.captainEnterprise?.mobile
            ),
            valueOrDefault(row.shippingRequirementAccept?.shipInfos?.[0]?.ton),
            formatDuration(row.platClue?.stayDurationSeconds),
            statusLabel(row.platClue?.state)
        ]
    },
    {
        type: E_TYPE.NEW_EMPTY_SHIP,
        title: "新增空船信息",
        headers: ["空船始发地", "目的地", "预计空船时间", "滞留时间", "处理状态"],
        renderRow: row => [
            valueOrDefault(row.emptyShipVo?.originPorts?.[0]?.portName),
            valueOrDefault(row.emptyShipVo?.destPorts?.[0]?.portName),
            row.emptyShipVo?.emptyShip?.emptyStartTime && row.emptyShipVo.emptyShip.emptyEndTime
                ? `${dateFormat(row.emptyShipVo.emptyShip.emptyStartTime, DATE_PICKER_FORMAT)} 至 ${dateFormat(
                      row.emptyShipVo.emptyShip.emptyEndTime,
                      DATE_PICKER_FORMAT
                  )}`
                : DEFAULT_VALUE,
            formatDuration(row.platClue?.stayDurationSeconds),
            statusLabel(row.platClue?.state)
        ]
    }
];

const normalizeUnPlayed = (payload: ClueDashboardUnPlayed) =>
    payload.flatMap(item => {
        if ("platClues" in item) return item.platClues || [];
        return [item];
    });

/** 线索大屏：保留 Vue screen/un-played 接口，并提供轮询替代的显式刷新入口。 */
export function ClueDashboardPage({
    loadScreen = loadClueDashboardData,
    loadUnPlayed = loadClueDashboardUnPlayed
}: ClueDashboardPageProps) {
    const [data, setData] = useState<ClueDashboardData | null>(null);
    const [unPlayedCount, setUnPlayedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);
    const [speechSettingsOpen, setSpeechSettingsOpen] = useState(false);
    const [speechCount, setSpeechCount] = useState(() => readSpeechCount());
    const [speechCountDraft, setSpeechCountDraft] = useState<number | undefined>(speechCount);

    const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

    /** 打开设置时重新读取已保存值，避免弹窗沿用上次未提交的草稿。 */
    const openSpeechSettings = () => {
        const storedCount = readSpeechCount();
        setSpeechCount(storedCount);
        setSpeechCountDraft(storedCount);
        setSpeechSettingsOpen(true);
    };

    /** 关闭设置时丢弃未提交草稿，保持旧 Vue closeDialog 的回滚语义。 */
    const closeSpeechSettings = () => {
        setSpeechCountDraft(readSpeechCount());
        setSpeechSettingsOpen(false);
    };

    /** 保存播报次数并通知当前页面立即采用新的配置。 */
    const saveSpeechSettings = () => {
        if (speechCountDraft == null) {
            setError("请选择次数");
            return;
        }
        localStorage.setItem(SPEECH_COUNT_STORAGE_KEY, String(speechCountDraft));
        setSpeechCount(speechCountDraft);
        setSpeechSettingsOpen(false);
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError("");

        // 待播报接口失败不应阻断大屏主体；Vue 版本同样将播报初始化与主体数据分开处理。
        void Promise.all([loadScreen(), loadUnPlayed().catch(() => [] as ClueDashboardUnPlayed)])
            .then(([screenData, unPlayed]) => {
                if (!active) return;
                setData(screenData);
                setUnPlayedCount(normalizeUnPlayed(unPlayed).length);
            })
            .catch(() => {
                if (!active) return;
                setData(null);
                setUnPlayedCount(0);
                setError("线索大屏加载失败");
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [loadScreen, loadUnPlayed, refreshKey]);

    return (
        <section aria-label="clue-dashboard-page" className="flex min-h-full flex-col gap-4">
            <Card
                title="船务平台线索大屏"
                extra={
                    <Space>
                        <Tag color="blue">实时播报兼容模式</Tag>
                        <span aria-label="当前重复播报次数">播报 {speechCount} 次</span>
                        <span aria-live="polite">待播报 {unPlayedCount} 条</span>
                        <Button aria-label="播报设置" onClick={openSpeechSettings}>
                            设置
                        </Button>
                        <Button onClick={refresh} loading={loading}>
                            刷新
                        </Button>
                    </Space>
                }
            >
                <p>按 Vue 线索接口展示新注册用户、找船需求、报价和空船信息。</p>
            </Card>

            {loading ? <Card loading={false}>线索大屏加载中</Card> : null}
            {error ? (
                <Alert
                    role="alert"
                    message={error}
                    type="error"
                    showIcon
                    action={<Button onClick={refresh}>刷新</Button>}
                />
            ) : null}

            {!loading && !error ? (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {tables.map(table => {
                        const rows = data?.[table.type] || [];
                        return (
                            <Card key={table.type} title={table.title}>
                                {rows.length ? (
                                    <div className="overflow-x-auto">
                                        <table>
                                            <thead>
                                                <tr>
                                                    {table.headers.map(header => (
                                                        <th key={header}>{header}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row, rowIndex) => (
                                                    <tr key={`${row.platClue?.id || "row"}-${rowIndex}`}>
                                                        {table.renderRow(row).map((cell, cellIndex) => (
                                                            <td key={`${cell}-${cellIndex}`}>{cell}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <AdminTableEmpty />
                                )}
                            </Card>
                        );
                    })}
                </div>
            ) : null}

            <ZhDialog
                open={speechSettingsOpen}
                title="设置"
                width={400}
                onCancel={closeSpeechSettings}
                footer={
                    <Space>
                        <Button onClick={closeSpeechSettings}>取消</Button>
                        <Button type="primary" onClick={saveSpeechSettings}>
                            保存设置
                        </Button>
                    </Space>
                }
            >
                <label htmlFor="clue-speech-count">重复播报次数</label>
                <Select
                    id="clue-speech-count"
                    aria-label="重复播报次数"
                    allowClear
                    placeholder="请选择次数"
                    value={speechCountDraft}
                    options={SPEECH_COUNT_OPTIONS}
                    onChange={value => setSpeechCountDraft(value)}
                />
            </ZhDialog>
        </section>
    );
}

export default ClueDashboardPage;
